import { getApiUrl } from "../utils/envConfig";
import type { components, operations } from "./generated/schema";

// Request/response contracts sourced from the FastAPI OpenAPI schema via
// openapi-typescript (npm run gen:api-types). These types are generated
// from the backend Pydantic models, so they can't drift from what the API
// actually accepts and returns — CI fails the build if schema.ts is stale.
type APIKeyCreate = components["schemas"]["APIKeyCreate"];
type APIKeyResponse = components["schemas"]["APIKeyResponse"];
type APIKeyListResponse = components["schemas"]["APIKeyListResponse"];
type RevokeApiKeyResponse =
  operations["revoke_api_key_api_keys__key_id__delete"]["responses"][200]["content"]["application/json"];

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const { getAuth } = await import("firebase/auth");
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

export const createApiKey = async (
  payload: APIKeyCreate
): Promise<APIKeyResponse> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiUrl()}/api-keys/`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to create API key");
  }
  return response.json();
};

export const listApiKeys = async (
  idClient?: string
): Promise<APIKeyListResponse> => {
  const headers = await getAuthHeaders();
  // The workspace scope is required for a workspace admin (returns only that
  // workspace's customer keys) and optional for Dashmote staff — see
  // apps/api/src/routes/api_keys.py.
  const url = idClient
    ? `${getApiUrl()}/api-keys/?id_client=${encodeURIComponent(idClient)}`
    : `${getApiUrl()}/api-keys/`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to list API keys");
  }
  return response.json();
};

export const revokeApiKey = async (
  keyId: string
): Promise<RevokeApiKeyResponse> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiUrl()}/api-keys/${keyId}`, {
    method: "DELETE",
    headers,
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to revoke API key");
  }
  return response.json();
};
